import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MemberSchedule } from "@/hooks/schedules/useMemberSchedule";
import { Send, CalendarIcon, Info } from "lucide-react";
import { format, addDays, startOfWeek, endOfWeek, isSameDay } from "date-fns";
import { es } from "date-fns/locale";
import { cn } from "@/lib/utils";

const DAY_NAMES = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

interface Props {
  schedules: MemberSchedule[];
  gymId: string;
  onSubmit: (data: {
    schedule_id: string;
    gym_id: string;
    current_day: number;
    current_start_time: string;
    requested_day: number;
    requested_start_time: string;
    requested_end_time: string;
    original_date: string;
    replacement_date: string;
    reason?: string;
  }) => void;
  isPending: boolean;
}

// Get the next occurrence of a given day_of_week from today
const getNextOccurrence = (dayOfWeek: number): Date => {
  const today = new Date();
  const todayDay = today.getDay();
  let diff = dayOfWeek - todayDay;
  if (diff < 0) diff += 7;
  if (diff === 0) {
    // If today is the day, use today only if it's still upcoming (morning), otherwise next week
    return today;
  }
  return addDays(today, diff);
};

const formatDateShort = (dateStr: string) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const date = new Date(y, m - 1, d);
  return format(date, "EEE d/M", { locale: es });
};

export const ChangeRequestForm = ({ schedules, gymId, onSubmit, isPending }: Props) => {
  const [selectedSchedule, setSelectedSchedule] = useState(schedules[0]?.id || "");
  const [replacementDate, setReplacementDate] = useState<Date | undefined>();
  const [requestedStart, setRequestedStart] = useState("");
  const [requestedEnd, setRequestedEnd] = useState("");
  const [reason, setReason] = useState("");
  const [calendarOpen, setCalendarOpen] = useState(false);

  const schedule = schedules.find(s => s.id === selectedSchedule);

  // Calculate the original date (next occurrence of this schedule's day)
  const originalDate = useMemo(() => {
    if (!schedule) return null;
    return getNextOccurrence(schedule.day_of_week);
  }, [schedule]);

  // Week boundaries for the replacement date picker
  const weekStart = useMemo(() => {
    if (!originalDate) return new Date();
    return startOfWeek(originalDate, { weekStartsOn: 1 });
  }, [originalDate]);

  const weekEnd = useMemo(() => {
    if (!originalDate) return new Date();
    return endOfWeek(originalDate, { weekStartsOn: 1 });
  }, [originalDate]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!schedule || !replacementDate || !requestedStart || !requestedEnd || !originalDate) return;

    const origDateStr = format(originalDate, "yyyy-MM-dd");
    const replDateStr = format(replacementDate, "yyyy-MM-dd");

    onSubmit({
      schedule_id: schedule.id,
      gym_id: gymId,
      current_day: schedule.day_of_week,
      current_start_time: schedule.start_time,
      requested_day: replacementDate.getDay(),
      requested_start_time: requestedStart,
      requested_end_time: requestedEnd,
      original_date: origDateStr,
      replacement_date: replDateStr,
      reason: reason || undefined,
    });
    setReason("");
    setReplacementDate(undefined);
    setRequestedStart("");
    setRequestedEnd("");
  };

  return (
    <Card>
      <CardContent className="p-4">
        <form onSubmit={handleSubmit} className="space-y-3">
          {schedules.length > 1 && (
            <Select value={selectedSchedule} onValueChange={setSelectedSchedule}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar turno" />
              </SelectTrigger>
              <SelectContent>
                {schedules.map(s => (
                  <SelectItem key={s.id} value={s.id}>
                    {DAY_NAMES[s.day_of_week]} {s.start_time.slice(0, 5)} - {s.end_time.slice(0, 5)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Show which turn is being changed */}
          {schedule && originalDate && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border/50">
              <Info className="w-4 h-4 text-primary mt-0.5 shrink-0" />
              <div className="text-sm">
                <p className="text-foreground font-medium">
                  Cambiar turno del {DAY_NAMES[schedule.day_of_week]} {format(originalDate, "d/M", { locale: es })}
                </p>
                <p className="text-muted-foreground text-xs">
                  {schedule.start_time.slice(0, 5)} - {schedule.end_time.slice(0, 5)} · Solo esta semana
                </p>
              </div>
            </div>
          )}

          {/* Replacement date picker */}
          <div className="space-y-1.5">
            <label className="text-sm text-muted-foreground">¿A qué día querés moverlo?</label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !replacementDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {replacementDate
                    ? format(replacementDate, "EEEE d 'de' MMMM", { locale: es })
                    : "Elegir día de reemplazo"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 bg-black/90 border-white/10" align="start">
                <Calendar
                  mode="single"
                  selected={replacementDate}
                  onSelect={(date) => {
                    setReplacementDate(date);
                    setCalendarOpen(false);
                  }}
                  disabled={(date) => {
                    // Only allow dates within the same week, excluding the original date
                    if (date < weekStart || date > weekEnd) return true;
                    if (originalDate && isSameDay(date, originalDate)) return true;
                    if (date < new Date(new Date().setHours(0, 0, 0, 0))) return true;
                    return false;
                  }}
                  className={cn("p-3 pointer-events-auto bg-black/70 text-white rounded-lg border border-white/10")}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Time inputs */}
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Hora inicio</label>
              <Input type="time" value={requestedStart} onChange={e => setRequestedStart(e.target.value)} />
            </div>
            <div className="space-y-1">
              <label className="text-xs text-muted-foreground">Hora fin</label>
              <Input type="time" value={requestedEnd} onChange={e => setRequestedEnd(e.target.value)} />
            </div>
          </div>

          <Textarea
            placeholder="Motivo del cambio (opcional)"
            value={reason}
            onChange={e => setReason(e.target.value)}
            className="min-h-[60px]"
          />

          <Button type="submit" disabled={isPending || !replacementDate || !requestedStart || !requestedEnd} className="w-full">
            <Send className="w-4 h-4 mr-2" />
            Enviar solicitud
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};
